import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Bot,
  Layers,
  CreditCard,
  EllipsisVertical,
  Globe,
  History,
  KeyRound,
  LogOut,
  Package,
  PlayCircle,
  Sparkles,
  UserRound,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { signOut, useAuth } from '@/lib/auth';
import { redirectToLogin } from '@/lib/http';

export interface AdminNavItem {
  to: string;
  label: string;
  icon: React.ComponentType;
}

/** 后台导航按用途分组，和 shadcn dashboard 示例一样每组带一个小标题 */
export const ADMIN_NAV_GROUPS: { label: string; items: AdminNavItem[] }[] = [
  {
    label: '概览',
    items: [{ to: '/admin/stats', label: '统计', icon: BarChart3 }],
  },
  {
    label: '资源',
    items: [
      { to: '/admin/accounts', label: '账号管理', icon: Bot },
      { to: '/admin/subscriptions', label: '订阅管理', icon: Layers },
      { to: '/admin/proxies', label: '代理 IP 管理', icon: Globe },
      { to: '/admin/cards', label: '支付卡管理', icon: CreditCard },
      { to: '/admin/plans', label: '平台套餐管理', icon: Package },
    ],
  },
  {
    label: '运行',
    items: [{ to: '/admin/runs', label: '运行管理', icon: PlayCircle }],
  },
  {
    label: '系统',
    items: [
      { to: '/admin/members', label: '成员管理', icon: Users },
      { to: '/admin/logs', label: '操作记录', icon: History },
      { to: '/admin/ai-keys', label: 'AI 接口', icon: KeyRound },
    ],
  },
];

/** 当前路由对应的导航项，顶部标题栏用它显示页面名 */
export function useCurrentNavItem(): AdminNavItem | undefined {
  const { pathname } = useLocation();
  return ADMIN_NAV_GROUPS.flatMap((group) => group.items).find(
    (item: AdminNavItem) => pathname.startsWith(item.to),
  );
}

/** 侧栏底部的当前用户：头像 + 姓名 + 邮箱，点开是我的订阅和退出登录 */
function NavUser() {
  const { user } = useAuth();
  const { isMobile } = useSidebar();
  const [signingOut, setSigningOut] = useState<boolean>(false);

  if (!user) {
    return null;
  }

  const handleSignOut = async (): Promise<void> => {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      toast.error('退出登录失败，请稍后重试');
      setSigningOut(false);
      return;
    }
    redirectToLogin();
  };

  const avatar = (
    <Avatar className="h-8 w-8 rounded-lg">
      <AvatarImage src={user.avatar} alt={user.name} />
      <AvatarFallback className="rounded-lg">
        {user.name.slice(0, 1)}
      </AvatarFallback>
    </Avatar>
  );
  const identity = (
    <div className="grid flex-1 text-left text-sm leading-tight">
      <span className="truncate font-medium">{user.name}</span>
      {user.email && (
        <span className="truncate text-xs text-muted-foreground">
          {user.email}
        </span>
      )}
    </div>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {avatar}
              {identity}
              <EllipsisVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5">
                {avatar}
                {identity}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/my">
                  <UserRound />
                  我的订阅
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              disabled={signingOut}
              onClick={() => void handleSignOut()}
            >
              <LogOut />
              {signingOut ? '退出中...' : '退出登录'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild size="lg">
              <Link to="/admin/stats">
                <span className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Sparkles className="size-4" />
                </span>
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-sm font-semibold">
                    AI 账号管理
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    团队 AI 资产控制台
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {ADMIN_NAV_GROUPS.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item: AdminNavItem) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.label}
                      isActive={pathname.startsWith(item.to)}
                    >
                      <NavLink
                        to={item.to}
                        // 手机上侧栏是抽屉，点完导航要自己收起来
                        onClick={() => isMobile && setOpenMobile(false)}
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
